import { useState } from 'react';
import { Github, Loader2, LogOut, RefreshCw, Star, Lock, Globe } from 'lucide-react';
import { useGitHubAuth, type GitHubRepo } from '@/hooks/useGitHubAuth';
import { cn } from '@/lib/utils';

interface GitHubRepoSelectorProps {
  onSelect?: (repo: GitHubRepo) => void;
  selectedUrl?: string;
}

export function GitHubRepoSelector({ onSelect, selectedUrl }: GitHubRepoSelectorProps) {
  const { isAuthenticated, repos, isLoading, error, login, logout, refetch } = useGitHubAuth();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredRepos = repos.filter(repo =>
    repo.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    repo.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isAuthenticated) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-secondary/30 p-4">
        <div className="flex flex-col items-center gap-3 text-center">
          <Github className="h-8 w-8 text-muted-foreground" />
          <div>
            <p className="text-sm font-medium">GitHubと連携</p>
            <p className="text-xs text-muted-foreground">
              リポジトリを選択してプロジェクトにリンク
            </p>
          </div>
          <button
            type="button"
            onClick={login}
            className="inline-flex items-center gap-2 rounded-md bg-[#24292f] px-4 py-2 text-sm font-medium text-white hover:bg-[#24292f]/90"
          >
            <Github className="h-4 w-4" />
            GitHubでログイン
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border p-3">
        <div className="flex items-center gap-2">
          <Github className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">リポジトリを選択</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refetch}
            disabled={isLoading}
            className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
            title="更新"
          >
            <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
          </button>
          <button
            type="button"
            onClick={logout}
            className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
            title="ログアウト"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="p-3">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="リポジトリを検索..."
          className="mb-3 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        />

        {error && (
          <div className="mb-3 rounded-md bg-destructive/10 p-2 text-xs text-destructive">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="max-h-60 space-y-1 overflow-y-auto">
            {filteredRepos.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {searchQuery ? '該当するリポジトリがありません' : 'リポジトリがありません'}
              </p>
            ) : (
              filteredRepos.map((repo) => (
                <button
                  key={repo.id}
                  type="button"
                  onClick={() => onSelect?.(repo)}
                  className={cn(
                    "w-full rounded-md p-2 text-left transition-colors hover:bg-secondary",
                    selectedUrl === repo.url && "bg-primary/10 border border-primary/30"
                  )}
                >
                  <div className="flex items-center gap-2">
                    {repo.isPrivate ? (
                      <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                    ) : (
                      <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                    <span className="flex-1 truncate text-sm font-medium">
                      {repo.name}
                    </span>
                    {repo.language && (
                      <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">
                        {repo.language}
                      </span>
                    )}
                    {repo.stars > 0 && (
                      <span className="flex items-center gap-0.5 text-xs text-muted-foreground">
                        <Star className="h-3 w-3" />
                        {repo.stars}
                      </span>
                    )}
                  </div>
                  {repo.description && (
                    <p className="mt-1 truncate text-xs text-muted-foreground">
                      {repo.description}
                    </p>
                  )}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
